import { formatElapsed } from './time';

export type WorkPhase = 'warmup' | 'accumulate' | 'closing' | 'achieved' | 'overtime';

export interface PhaseResult {
  phase: WorkPhase;
  ratio: number;
  display: string;
  glowOpacity: number;
  breatheDuration: string;
}

const WARMUP_THRESHOLD = 0.125;
const CLOSING_THRESHOLD = 0.9;
const ACHIEVED_WINDOW_MS = 1000;

export function getWorkPhase(elapsedMs: number, targetMs: number, isPaused: boolean): PhaseResult {
  const safeElapsed = Math.max(0, elapsedMs);

  if (targetMs <= 0) {
    return {
      phase: 'warmup',
      ratio: 0,
      display: formatElapsed(Math.floor(safeElapsed / 1000)),
      glowOpacity: 0,
      breatheDuration: '2s',
    };
  }

  const ratio = safeElapsed / targetMs;
  const overMs = safeElapsed - targetMs;

  if (ratio < WARMUP_THRESHOLD) {
    const sec = Math.floor(safeElapsed / 1000);
    return {
      phase: 'warmup',
      ratio,
      display: formatElapsed(sec),
      glowOpacity: 0,
      breatheDuration: isPaused ? '0s' : '4s',
    };
  }

  if (ratio < CLOSING_THRESHOLD) {
    const h = Math.floor(safeElapsed / 3600000);
    const m = Math.floor((safeElapsed % 3600000) / 60000);
    return {
      phase: 'accumulate',
      ratio,
      display: `${h}h ${m}m`,
      glowOpacity: 0.2,
      breatheDuration: isPaused ? '0s' : '2s',
    };
  }

  if (ratio < 1.0) {
    const remainMs = targetMs - safeElapsed;
    const remainMin = Math.ceil(remainMs / 60000);
    return {
      phase: 'closing',
      ratio,
      display: `${remainMin}m 남음`,
      glowOpacity: 0.4,
      breatheDuration: isPaused ? '0s' : '1.2s',
    };
  }

  if (overMs < ACHIEVED_WINDOW_MS) {
    return {
      phase: 'achieved',
      ratio,
      display: '0m 남음',
      glowOpacity: 1,
      breatheDuration: '0s',
    };
  }

  const overMin = Math.floor(overMs / 60000);
  let display: string;
  if (overMin < 60) {
    display = `+${overMin}m`;
  } else {
    const oh = Math.floor(overMin / 60);
    const om = overMin % 60;
    display = `+${oh}h ${om}m`;
  }

  return {
    phase: 'overtime',
    ratio,
    display,
    glowOpacity: 1,
    breatheDuration: isPaused ? '0s' : '2s',
  };
}
